import React, { useEffect, useState, useRef, useCallback } from 'react';
import AsyncSelect from 'react-select/async';
import Select from 'react-select';
import { useNavigate } from "react-router-dom";
import { customFetch } from '../apiClient';
import IpdAdvanceReceipt from './IpdAdvanceReceipt';

// Helper function: Current Date & Time (datetime-local format)
const getCurrentDateTimeLocal = () => {
  const now = new Date();
  return new Date(now.getTime() - now.getTimezoneOffset() * 60000).toISOString().slice(0, 16);
};

const getInitialIpdAdvForm = () => ({
  IPD_ID: null,
  Adv_Amt: "",
  Remarks: "",
  PayMode: "Cash",
  ChqNo: "",
  ChqDate: "",
  ENTRYDATE: getCurrentDateTimeLocal()
});

const paymentModeOptions = [
  { value: "Cash", label: "Cash" },
  { value: "Card", label: "Card" },
  { value: "UPI", label: "UPI" },
  { value: "Cheque", label: "Cheque" }
];

const IpdAdvanceForm = ({ onSubmitSuccess }) => {
 
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [advanceReceiptData, setAdvanceReceiptData] = useState(null);
  const [showAdvanceReceipt, setShowAdvanceReceipt] = useState(false);
  const [companyDetails, setCompanyDetails] = useState(null);
  const [touched, setTouched] = useState({});
  const [selectedPatientLabel, setSelectedPatientLabel] = useState(null);

  // States for Existing Advances & Edit Modal
  const [existingAdvances, setExistingAdvances] = useState([]);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [editId, setEditId] = useState(null);
  const [editData, setEditData] = useState({});

  const navigate = useNavigate();
  const formRef = useRef(null);

const [form, setForm] = useState(getInitialIpdAdvForm);
  // ... baaki existing states waisi hi rahengi ...

  // 2. NAYA: Har 1 minute mein time update karne wala timer
  useEffect(() => {
    const intervalId = setInterval(() => {
      setForm(prev => {
        // Agar user ne field ko touch/edit NAHI kiya hai, tabhi live time update karega
        if (!touched.ENTRYDATE) {
          return { ...prev, ENTRYDATE: getCurrentDateTimeLocal() };
        }
        return prev;
      });
    }, 60000); // 60000 ms = 1 minute

    // Cleanup function
    return () => clearInterval(intervalId);
  }, [touched.ENTRYDATE]);




  // Fetch company details
  useEffect(() => {
    const token = sessionStorage.getItem("token");
    const hospitalId = sessionStorage.getItem("hospitalId");

  if (hospitalId && token) {
      customFetch(`/Company/${hospitalId}`)
        .then(res => {
          if (!res.ok) throw new Error("Failed to load company details");
          return res.json();
        })
        .then(data => setCompanyDetails(data))
        .catch(err => console.error("Company details fetch error:", err));
    }
  }, []);

  // Auto-focus first input
  useEffect(() => {
    if (formRef.current) {
      const firstInput = formRef.current.querySelector(
        'input:not([disabled]):not([type="hidden"]), select:not([disabled]), textarea, button:not([disabled])'
      );
      firstInput?.focus();
    }
  }, []);

  // Auto-dismiss alerts
  useEffect(() => {
    if (success) {
      const timer = setTimeout(() => setSuccess(""), 3000);
      return () => clearTimeout(timer);
    }
  }, [success]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setForm(prev => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value
    }));
    setTouched(prev => ({ ...prev, [name]: true }));
  };

  const handlePaymentModeChange = (selectedOption) => {
    setForm(prev => ({ ...prev, PayMode: selectedOption ? selectedOption.value : "Cash" }));
  };

  // Fetch Existing Advances
  const loadExistingAdvances = async (ipdId) => {
    if (!ipdId) {
      setExistingAdvances([]);
      return;
    }
   try {
      const res = await customFetch(`/IpdAdvDt/search-by-ipd?ipdId=${ipdId}`);
      if (res.ok) {
        const data = await res.json();
        setExistingAdvances(data);
      } else {
        setExistingAdvances([]);
      }
    } catch (e) {
      console.error("Failed to load existing advances", e);
      setExistingAdvances([]);
    }
  };

const loadIpdOptions = async (inputValue) => {
    if (!inputValue) return [];
   try {
      const res = await customFetch(`/Ipds/list?search=${encodeURIComponent(inputValue)}`);
      
      if (!res.ok) throw new Error("Failed");
      const data = await res.json();
      
      return data.map((item) => {
        // ✅ Changed ipD_Date to regDate 
        const regDateFormatted = item.regDate 
            ? new Date(item.regDate).toLocaleDateString('en-IN', { 
                day: '2-digit', month: 'short', year: 'numeric' 
              }) 
            : "N/A";

        return {
          // ✅ Label updated to show Reg Date
          label: `${item.name} (${item.mobileNo}) | UHID: ${item.uhiD_No} | IPD No: ${item.ipD_SRNO} | Reg Date: ${regDateFormatted}`,
          value: item.ipD_ID,
          admDate: item.regDate, // Isko regDate se map kar diya taaki form mein save karte waqt yahi date jaye
          name: item.name,
        };
      });
    } catch { 
      return []; 
    }
  };

  const handleIpdChange = (selectedOption) => {
    const ipdId = selectedOption ? selectedOption.value : null;
    setForm(prev => ({
      ...prev,
      IPD_ID: ipdId
    }));
    setSelectedPatientLabel(selectedOption);
    setTouched(prev => ({ ...prev, IPD_ID: true }));
    
    loadExistingAdvances(ipdId);
    cancelEdit();
  };

  // Keyboard navigation
  const handleKeyDown = useCallback((e) => {
    if (e.key !== 'Enter') return;
    if (e.target.closest('.react-select__menu') || isEditModalOpen) return;

    const focusableSelector = 'input:not([disabled]):not([type="hidden"]), textarea:not([disabled]), button:not([disabled])';
    const focusableElements = Array.from(formRef.current?.querySelectorAll(focusableSelector) || []);
    
    if (focusableElements.length === 0) return;
    const currentIndex = focusableElements.indexOf(e.target);

    if (currentIndex === focusableElements.length - 1) {
      return; 
    }

    if (currentIndex >= 0) {
      e.preventDefault();
      const nextIndex = currentIndex + 1;
      focusableElements[nextIndex]?.focus();
    }
  }, [isEditModalOpen]);

  useEffect(() => {
    const handleGlobalKeyDown = (e) => {
      if (formRef.current && e.target.closest('form') === formRef.current) {
        handleKeyDown(e);
      }
    };
    document.addEventListener('keydown', handleGlobalKeyDown);
    return () => document.removeEventListener('keydown', handleGlobalKeyDown);
  }, [handleKeyDown]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setSuccess("");

    if (!form.IPD_ID) {
      setError("Please select a patient");
      setTouched({ IPD_ID: true });
      return;
    }

    if (!form.Adv_Amt || Number(form.Adv_Amt) <= 0) {
      setError("Please enter a valid advance amount");
      setTouched({ Adv_Amt: true });
      return;
    }

    setLoading(true);

    try {
      const token = sessionStorage.getItem("token");
      const hid = sessionStorage.getItem("hospitalId");

      if (!token || !hid) {
        throw new Error("Session expired. Please login again.");
      }

      let formattedDate = form.ENTRYDATE || getCurrentDateTimeLocal();
      if (formattedDate.includes(" ")) formattedDate = formattedDate.replace(" ", "T");

      const payload = {
        IPD_ID: Number(form.IPD_ID),
        Adv_Amt: Number(form.Adv_Amt),
        Remarks: form.Remarks || null,
        PayMode: form.PayMode,
        ChqNo: form.ChqNo || null,
        ChqDate: form.ChqDate || null,
        ENTRYDATE: formattedDate,
        HID: Number(hid)
      };
const res = await customFetch(`/IpdAdvDt`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json"
        },
        body: JSON.stringify(payload)
      });

      if (!res.ok) {
        const msg = await res.text();
        throw new Error(msg || "Failed to save advance");
      }

      const data = await res.json();
      setAdvanceReceiptData(data);
      setShowAdvanceReceipt(true);
      
     setForm(getInitialIpdAdvForm());
      setSelectedPatientLabel(null);
      setExistingAdvances([]); 
      setTouched({});
      setSuccess("Advance payment saved successfully!");

      if (onSubmitSuccess) onSubmitSuccess(data);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleReset = () => {
    setForm(getInitialIpdAdvForm());
    setSelectedPatientLabel(null);
    setExistingAdvances([]);
    setTouched({});
    setError("");
    setSuccess("");
  };

  const handleButtonClick = () => {
    navigate('/clinical/advance-table'); // ✅ Clinical Layout me bhejega
  };

  // ✅ Edit Modal Logic
  const startEditing = (record) => {
    setEditId(record.advID);
    setEditData({
      adv_Amt: record.adv_Amt || "",
      remarks: record.remarks || "",
      payMode: record.payMode || "Cash",
      chqNo: record.chqNo || "",
      chqDate: record.chqDate ? record.chqDate.substring(0, 10) : "",
      recvDate: record.recvDate ? record.recvDate.substring(0, 10) : "",
      entrydate: (record.entrydate || record.ENTRYDATE) ? (record.entrydate || record.ENTRYDATE).substring(0, 16) : "", 
    });
    setIsEditModalOpen(true);
  };

  const handleEditChange = (field, value) => {
    setEditData(prev => {
      const updated = { ...prev, [field]: value };
      if (field === "payMode" && (value === "Cash" || value === "UPI" || value === "Card")) {
        updated.chqNo = "";
        updated.chqDate = "";
      }
      return updated;
    });
  };

  const cancelEdit = () => {
    setIsEditModalOpen(false);
    setEditId(null);
    setEditData({});
  };

  const saveExistingRecord = async () => {
    if (!editId) return;
    const amtNum = parseFloat(editData.adv_Amt);
    if (isNaN(amtNum) || amtNum <= 0) {
      setError("Advance amount must be a positive number.");
      return;
    }
    setLoading(true);
    try {
     
      const originalRecord = existingAdvances.find(r => r.advID === editId);
      
      const updatedRecord = {
        ...originalRecord,
        adv_Amt: amtNum,
        recvDate: editData.recvDate || originalRecord.recvDate,
        remarks: editData.remarks,
        payMode: editData.payMode,
        chqNo: editData.chqNo || null,
        chqDate: editData.chqDate || null,
        ENTRYDATE: editData.entrydate || null 
      };

   const res = await customFetch(`/IpdAdvDt/${editId}`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json"
        },
        body: JSON.stringify(updatedRecord),
      });

      if (!res.ok) throw new Error("Failed to update record.");

      setSuccess("Record updated successfully.");
      setExistingAdvances(prev => prev.map(r => r.advID === editId ? { ...r, ...updatedRecord } : r));
      cancelEdit();
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  };

  const deleteExistingRecord = async (id) => {
    if (!window.confirm("Are you sure you want to delete this advance payment?")) return;
    setLoading(true);
   try {
      const res = await customFetch(`/IpdAdvDt/${id}`, {
        method: "DELETE"
      });
      if (!res.ok) throw new Error("Failed to delete record.");
      setSuccess("Record deleted successfully.");
      setExistingAdvances(prev => prev.filter(r => r.advID !== id));
      if (editId === id) cancelEdit();
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  };

  const handleAdvancePreview = async (record) => {
    try {
      const res = await customFetch(`/IpdAdvDt/receipt/${record.advID}`);
      if (!res.ok) throw new Error("Failed to load receipt data");
      const data = await res.json();
      setAdvanceReceiptData(data);
      setShowAdvanceReceipt(true);
    } catch (err) {
      setError("Failed to load receipt");
    }
  };

  // Scaled Down React-Select Styles
  const selectStyles = {
    control: (base, state) => ({
      ...base,
      minHeight: '30px',
      fontSize: '11px',
      borderRadius: '6px',
      borderColor: state.isFocused ? 'var(--theme-color, #0088cc)' : '#cbd5e0',
      boxShadow: state.isFocused ? '0 0 0 2px rgba(0, 0, 0, 0.05)' : 'none',
      '&:hover': { borderColor: 'var(--theme-color, #0088cc)' }
    }),
    valueContainer: (base) => ({ ...base, padding: '0 6px' }),
    input: (base) => ({ ...base, margin: '0', padding: '0' }),
    dropdownIndicator: (base) => ({ ...base, padding: '4px' }),
    clearIndicator: (base) => ({ ...base, padding: '4px' }),
    menu: (base) => ({
      ...base,
      zIndex: 9999,
      borderRadius: '6px',
      boxShadow: '0 4px 15px rgba(0, 0, 0, 0.1)',
      border: '1px solid #e0e6ed',
      fontSize: '11px'
    }),
    option: (base, state) => ({
      ...base,
      padding: '6px 10px',
      backgroundColor: state.isSelected 
        ? 'var(--theme-color, #0088cc)' 
        : state.isFocused 
          ? 'color-mix(in srgb, var(--theme-color, #0088cc), white 90%)' 
          : 'white',
      color: state.isSelected ? 'white' : '#2c3e50',
      cursor: 'pointer',
      '&:active': { backgroundColor: 'var(--theme-color, #0088cc)' },
    }),
    placeholder: (base) => ({ ...base, color: '#a0aec0' })
  };

  return (
    <div className="advance-form-container compact-ui">
      <div className="advance-card">
        {/* Header */}
        <div className="advance-header">
          <div className="header-left">
            <div className="header-icon">
              <i className="bi bi-cash-coin"></i>
            </div>
            <div className="header-text">
              <h5>IPD Advance Payment</h5>
              <p>Record and manage advance payments</p>
            </div>
          </div>
          <button type="button" className="btn-view-list" onClick={handleButtonClick} title="Advance History">
            <i className="bi bi-list-ul"></i>
            Advance History
          </button>
        </div>

        {/* Alerts */}
        {error && (
          <div className="alert-custom alert-danger slide-in">
            <i className="bi bi-exclamation-triangle-fill"></i>
            <span>{error}</span>
            <button className="alert-close" onClick={() => setError("")}><i className="bi bi-x"></i></button>
          </div>
        )}
        {success && (
          <div className="alert-custom alert-success slide-in">
            <i className="bi bi-check-circle-fill"></i>
            <span>{success}</span>
            <button className="alert-close" onClick={() => setSuccess("")}><i className="bi bi-x"></i></button>
          </div>
        )}

        {/* Main Content (Stacked Layout) */}
        <div className="main-content">
          {/* Top Panel: Form */}
          <div className="form-panel">
            <form ref={formRef} onSubmit={handleSubmit} className="advance-form">
              <div className="form-section">
                <div className="section-title">
                  <i className="bi bi-plus-circle"></i>
                  <span>New Advance Entry</span>
                </div>
                <div className="section-content">
                  <div className="form-row search-date-row">
                    <div className="form-group">
                      <label>
                        <i className="bi bi-person-search"></i> Search Patient <span className="required">*</span>
                      </label>
                      <AsyncSelect
                        cacheOptions
                        defaultOptions
                        loadOptions={loadIpdOptions}
                        onChange={handleIpdChange}
                        placeholder="Search patient..."
                        value={selectedPatientLabel}
                        isClearable
                        tabIndex={1}
                        isDisabled={loading}
                        styles={selectStyles}
                      />
                    </div>
                    <div className="form-group">
                      <label>
                        <i className="bi bi-calendar-event"></i> Entry Date <span className="required">*</span>
                      </label>
                      <input
                        name="ENTRYDATE"
                        type="datetime-local"
                        value={form.ENTRYDATE}
                        onChange={handleChange}
                        className="form-control-custom"
                        tabIndex={2}
                        disabled={loading}
                        required
                      />
                    </div>
                  </div>

                  <div className="form-row two-col">
                    <div className="form-group">
                      <label>
                        <i className="bi bi-currency-rupee"></i> Advance Amount <span className="required">*</span>
                      </label>
                      <input
                        name="Adv_Amt"
                        type="number"
                        value={form.Adv_Amt}
                        onChange={handleChange}
                        placeholder="5000"
                        className="form-control-custom"
                        tabIndex={3}
                        min="0"
                        step="0.01"
                        disabled={loading}
                        required
                      />
                    </div>
                    <div className="form-group">
                      <label>
                        <i className="bi bi-chat-left-text"></i> Remarks
                      </label>
                      <textarea
                        name="Remarks"
                        value={form.Remarks}
                        onChange={handleChange}
                        placeholder="Comments..."
                        className="form-control-custom custom-textarea"
                        tabIndex={4}
                        rows={1}
                        disabled={loading}
                        maxLength={200}
                      />
                    </div>
                  </div>

                  <div className="form-row three-col">
                    <div className="form-group">
                      <label>
                        <i className="bi bi-credit-card"></i> Payment Mode <span className="required">*</span>
                      </label>
                      <Select 
                        options={paymentModeOptions} 
                        value={paymentModeOptions.find(opt => opt.value === form.PayMode)} 
                        onChange={handlePaymentModeChange} 
                        styles={selectStyles} 
                        isDisabled={loading}
                      />
                    </div>
                    {form.PayMode === "Cheque" && (
                      <>
                        <div className="form-group">
                          <label>Cheque No</label>
                          <input name="ChqNo" type="text" value={form.ChqNo} onChange={handleChange} className="form-control-custom" disabled={loading} />
                        </div>
                        <div className="form-group">
                          <label>Cheque Date</label>
                          <input name="ChqDate" type="date" value={form.ChqDate} onChange={handleChange} className="form-control-custom" disabled={loading} />
                        </div>
                      </>
                    )}
                  </div>
                </div>
              </div>
              <div className="form-actions">
                <button type="button" className="btn-secondary" onClick={handleReset} disabled={loading}>
                  <i className="bi bi-arrow-clockwise"></i> Reset
                </button>
                <button type="submit" className="btn-primary" disabled={loading || !form.IPD_ID || !form.Adv_Amt}>
                  {loading ? <><span className="spinner-border spinner-border-sm me-2"></span>Processing...</> : <><i className="bi bi-check-circle"></i> Submit Advance</>}
                </button>
              </div>
            </form>
          </div>

          {/* Bottom Panel: Existing Advances Table (View Only, NO inline editing) */}
          {form.IPD_ID && (
            <div className="existing-records-panel">
              <div className="existing-records-section">
                <div className="section-title">
                  <i className="bi bi-clock-history"></i>
                  <span>Previous Advances</span>
                  {existingAdvances.length > 0 && <span className="badge-count-small">{existingAdvances.length}</span>}
                </div>
                <div className="existing-records-content">
                  {existingAdvances.length === 0 ? (
                    <div className="empty-state-small">
                      <i className="bi bi-inbox"></i>
                      <p>No previous advances found</p>
                    </div>
                  ) : (
                    <div className="table-responsive">
                      <table className="records-table">
                        <thead>
                          <tr>
                            <th style={{ width: '40px' }} className="text-center">
                              <i className="bi bi-eye"></i>
                            </th>
                            <th>Amount</th>
                            <th>Entry Date</th>
                            <th>Received Date</th>
                            <th>Payment Mode</th>
                            <th>Cheque No.</th>
                            <th>Cheque Date</th>
                            <th>Remarks</th>
                            <th style={{ width: '80px' }} className="text-center">
                              <i className="bi bi-tools"></i> Actions
                            </th>
                          </tr>
                        </thead>
                        <tbody>
                          {existingAdvances.map((record) => (
                            <tr key={record.advID}>
                              <td className="text-center">
                                <button className="btn-icon btn-preview" onClick={() => handleAdvancePreview(record)} title="Preview Receipt">
                                  <i className="bi bi-eye-fill"></i>
                                </button>
                              </td>
                              <td>
                                <span className="amount-badge">
                                  <i className="bi bi-currency-rupee"></i>
                                  {record.adv_Amt?.toFixed(2)}
                                </span>
                              </td>
                              <td>
                                <span className="date-text">
                                  <i className="bi bi-calendar-event"></i>
                                  {(record.entrydate || record.ENTRYDATE) ? 
                                    new Date(record.entrydate || record.ENTRYDATE).toLocaleString('en-IN', {
                                      day: '2-digit', month: '2-digit', year: 'numeric',
                                      hour: '2-digit', minute: '2-digit', hour12: true
                                    }) : '-'
                                  }
                                </span>
                              </td>
                              <td>
                                <span className="date-text">
                                  <i className="bi bi-calendar-check"></i>
                                  {record.recvDate ? new Date(record.recvDate).toLocaleDateString('en-IN') : '-'}
                                </span>
                              </td>
                              <td>
                                <span className={`payment-badge payment-${record.payMode?.toLowerCase()}`}>
                                  {record.payMode === 'Cash' && <i className="bi bi-cash"></i>}
                                  {record.payMode === 'Card' && <i className="bi bi-credit-card"></i>}
                                  {record.payMode === 'UPI' && <i className="bi bi-phone"></i>}
                                  {record.payMode === 'Cheque' && <i className="bi bi-receipt"></i>}
                                  {record.payMode}
                                </span>
                              </td>
                              <td>{record.chqNo || <span className="text-muted">-</span>}</td>
                              <td>{record.chqDate ? new Date(record.chqDate).toLocaleDateString('en-IN') : <span className="text-muted">-</span>}</td>
                              <td><span className="remarks-text">{record.remarks || <span className="text-muted">-</span>}</span></td>
                              <td className="text-center">
                                <div className="action-buttons">
                                  <button className="btn-icon btn-edit" onClick={() => startEditing(record)} title="Edit">
                                    <i className="bi bi-pencil-square"></i>
                                  </button>
                                  <button className="btn-icon btn-delete" onClick={() => deleteExistingRecord(record.advID)} title="Delete">
                                    <i className="bi bi-trash"></i>
                                  </button>
                                </div>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Edit Modal (Popup) */}
      {isEditModalOpen && (
        <div className="modal-overlay">
          <div className="modal-content slide-in">
            <div className="modal-header">
              <h5><i className="bi bi-pencil-square"></i> Edit Advance Payment</h5>
              <button className="btn-modal-close" onClick={cancelEdit}><i className="bi bi-x"></i></button>
            </div>
            <div className="modal-body">
              <div className="form-row two-col">
                <div className="form-group">
                  <label>Advance Amount</label>
                  <input type="number" className="form-control-custom" value={editData.adv_Amt} onChange={e => handleEditChange('adv_Amt', e.target.value)} min="0" step="0.01" />
                </div>
                <div className="form-group">
                  <label>Entry Date</label>
                  <input type="datetime-local" className="form-control-custom" value={editData.entrydate || ""} onChange={e => handleEditChange('entrydate', e.target.value)} />
                </div>
              </div>
              <div className="form-row two-col">
                <div className="form-group">
                  <label>Payment Mode</label>
                  <select className="form-control-custom" value={editData.payMode} onChange={e => handleEditChange('payMode', e.target.value)}>
                    <option value="Cash">Cash</option>
                    <option value="UPI">UPI</option>
                    <option value="Card">Card</option>
                    <option value="Cheque">Cheque</option>
                  </select>
                </div>
                <div className="form-group">
                  <label>Received Date</label>
                  <input type="date" className="form-control-custom" value={editData.recvDate || ""} disabled />
                </div>
              </div>
              {editData.payMode === 'Cheque' && (
                <div className="form-row two-col">
                  <div className="form-group">
                    <label>Cheque No</label>
                    <input type="text" className="form-control-custom" value={editData.chqNo || ""} onChange={e => handleEditChange('chqNo', e.target.value)} />
                  </div>
                  <div className="form-group">
                    <label>Cheque Date</label>
                    <input type="date" className="form-control-custom" value={editData.chqDate || ""} onChange={e => handleEditChange('chqDate', e.target.value)} />
                  </div>
                </div>
              )}
              <div className="form-group" style={{ marginTop: '8px' }}>
                <label>Remarks</label>
                <input type="text" className="form-control-custom" value={editData.remarks || ""} onChange={e => handleEditChange('remarks', e.target.value)} placeholder="Add remarks..." />
              </div>
            </div>
            <div className="modal-footer form-actions">
              <button className="btn-secondary" onClick={cancelEdit}>Cancel</button>
              <button className="btn-primary" onClick={saveExistingRecord} disabled={loading}>
                {loading ? "Saving..." : "Save Changes"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Receipt Modal */}
      {companyDetails && showAdvanceReceipt && advanceReceiptData && (
        <IpdAdvanceReceipt
          show={showAdvanceReceipt}
          data={advanceReceiptData}
          companyDetails={companyDetails}
          onClose={() => setShowAdvanceReceipt(false)}
          onPrint={() => window.print()}
        />
      )}

      {/* CSS: SCALED DOWN COMPACT UI */}
      <style jsx>{`
        .compact-ui {
          /* This scaling simulates the 60% zoom natively */
          font-family: system-ui, -apple-system, sans-serif;
        }

        .advance-form-container {
          padding: 0;
          max-height: 100vh;
          overflow-y: auto;
        }

        .advance-card {
          background: white;
          border-radius: 8px;
          box-shadow: 0 2px 10px rgba(0, 0, 0, 0.05);
          border: 1px solid #e0e6ed;
          overflow: visible;
        }

        .main-content {
          display: flex;
          flex-direction: column;
          gap: 12px;
          padding: 12px 16px;
        }
        
        .form-panel, .existing-records-panel { width: 100%; }

        .existing-records-section {
          background: #f8f9fa;
          border: 1px solid #e0e6ed;
          border-radius: 6px;
          overflow: hidden;
        }

        .existing-records-content {
          padding: 12px;
          background: white;
          max-height: 350px;
          overflow-y: auto;
        }

        .badge-count-small {
          background: white; color: var(--theme-color, #0088cc);
          padding: 2px 6px; border-radius: 10px; font-size: 10px; font-weight: bold;
        }

        .empty-state-small { text-align: center; padding: 1.5rem 1rem; color: #9ca3af; }
        .empty-state-small i { font-size: 2rem; margin-bottom: 0.3rem; display: block; }
        
        /* Table Styles (Scaled Down) */
        .table-responsive { overflow-x: auto; }
        .records-table { width: 100%; border-collapse: separate; border-spacing: 0; font-size: 11px; }
        .records-table thead tr { background: var(--theme-color, #0088cc); }
        .records-table thead th {
          padding: 6px 8px; text-align: left; font-weight: 600; color: white;
          font-size: 10px; text-transform: uppercase; letter-spacing: 0.5px;
          white-space: nowrap; border: none;
        }
        .records-table thead th:first-child { border-top-left-radius: 6px; }
        .records-table thead th:last-child { border-top-right-radius: 6px; }
        .records-table tbody tr { background: white; transition: all 0.2s; }
        .records-table tbody tr:hover { background: #f8f9fa; transform: translateY(-1px); box-shadow: 0 2px 5px rgba(0,0,0,0.05); }
        .records-table tbody td { padding: 6px 8px; border-bottom: 1px solid #e9ecef; vertical-align: middle; }

        .text-center { text-align: center; }

        /* Badges & Text Styling */
        .amount-badge { display: inline-flex; align-items: center; gap: 0.2rem; background: #dcfce7; color: #166534; padding: 0.2rem 0.5rem; border-radius: 4px; font-weight: 600; font-size: 11px; }
        .payment-badge { display: inline-flex; align-items: center; gap: 0.25rem; padding: 0.2rem 0.5rem; border-radius: 4px; font-weight: 500; font-size: 10px; }
        .payment-cash { background: #d1fae5; color: #065f46; }
        .payment-card { background: #dbeafe; color: #1e40af; }
        .payment-upi { background: #fce7f3; color: #9f1239; }
        .payment-cheque { background: #fef3c7; color: #92400e; }
        .date-text { display: inline-flex; align-items: center; gap: 0.25rem; color: #4b5563; font-size: 11px; white-space: nowrap; }
        .date-text i { color: var(--theme-color, #0088cc); }
        .remarks-text { color: #4b5563; font-size: 11px; font-style: italic; }
        .text-muted { color: #9ca3af; font-size: 11px; }

        /* Action Buttons */
        .action-buttons { display: flex; gap: 0.4rem; justify-content: center; }
        .btn-icon { width: 24px; height: 24px; border: none; border-radius: 4px; display: flex; align-items: center; justify-content: center; cursor: pointer; transition: all 0.2s; font-size: 11px; }
        .btn-preview { background: #e0f2fe; color: #0369a1; }
        .btn-preview:hover { background: #0369a1; color: white; transform: translateY(-1px); }
        .btn-edit { background: #dbeafe; color: #1e40af; }
        .btn-edit:hover { background: #1e40af; color: white; transform: translateY(-1px); }
        .btn-delete { background: #fee2e2; color: #dc2626; }
        .btn-delete:hover { background: #dc2626; color: white; transform: translateY(-1px); }
        .btn-icon:disabled { opacity: 0.5; cursor: not-allowed; transform: none; }

        /* MODAL CSS */
        .modal-overlay {
          position: fixed; top: 0; left: 0; right: 0; bottom: 0;
          background: rgba(0, 0, 0, 0.4); z-index: 1000;
          display: flex; align-items: center; justify-content: center;
          backdrop-filter: blur(2px);
        }
        .modal-content {
          background: white; border-radius: 8px; width: 100%; max-width: 500px;
          box-shadow: 0 4px 20px rgba(0,0,0,0.15); display: flex; flex-direction: column;
        }
        .modal-header {
          padding: 10px 16px; border-bottom: 1px solid #e0e6ed;
          display: flex; justify-content: space-between; align-items: center;
          background: #f8f9fa; border-top-left-radius: 8px; border-top-right-radius: 8px;
        }
        .modal-header h5 { margin: 0; font-size: 13px; font-weight: 600; color: #2c3e50; display: flex; align-items: center; gap: 6px; }
        .modal-header h5 i { color: var(--theme-color, #0088cc); }
        .btn-modal-close { background: none; border: none; font-size: 18px; color: #6b7280; cursor: pointer; padding: 0; display: flex; }
        .btn-modal-close:hover { color: #e74c3c; }
        .modal-body { padding: 16px; }
        .modal-footer {
          padding: 10px 16px; border-top: 1px solid #e0e6ed;
          background: #f8f9fa; border-bottom-left-radius: 8px; border-bottom-right-radius: 8px;
          display: flex; justify-content: flex-end; gap: 8px; margin-top: 0; border-top: 1px solid #e0e6ed;
        }

        /* Animations */
        @keyframes slideIn { from { opacity: 0; transform: translateY(-10px); } to { opacity: 1; transform: translateY(0); } }
        .slide-in { animation: slideIn 0.2s ease; }

        /* Form Headers and General */
        .advance-header {
          background: var(--theme-color, #0088cc);
          padding: 10px 16px; display: flex; justify-content: space-between;
          align-items: center; border-bottom: 2px solid #00d4aa;
        }
        .header-left { display: flex; align-items: center; gap: 10px; }
        .header-icon {
          width: 32px; height: 32px; background: rgba(255, 255, 255, 0.2);
          border-radius: 6px; display: flex; align-items: center; justify-content: center;
          font-size: 16px; color: white;
        }
        .header-text h5 { margin: 0; color: white; font-size: 15px; font-weight: 600; line-height: 1.1; }
        .header-text p { margin: 0; color: rgba(255, 255, 255, 0.85); font-size: 10px; }

        .btn-view-list {
          padding: 6px 12px; border-radius: 6px; border: 1.5px solid rgba(255, 255, 255, 0.3);
          background: rgba(255, 255, 255, 0.15); color: white; font-size: 11px; font-weight: 600;
          cursor: pointer; transition: all 0.2s ease; display: flex; align-items: center; gap: 5px;
        }
        .btn-view-list:hover { background: white; color: var(--theme-color, #0088cc); transform: translateY(-1px); }

        .alert-custom { margin: 10px 16px 0; padding: 8px 12px; border-radius: 6px; display: flex; align-items: center; gap: 8px; font-size: 11px; font-weight: 500; position: relative; }
        .alert-danger { background: #fee; color: #c33; border: 1px solid #fcc; }
        .alert-success { background: #d4edda; color: #155724; border: 1px solid #c3e6cb; }
        .alert-close { position: absolute; right: 8px; background: transparent; border: none; cursor: pointer; opacity: 0.6; }

        .advance-form { padding: 0; }
       .form-section { 
  background: #ffffff; border: 1px solid #e0e6ed; border-radius: 8px; 
  overflow: visible; /* Yaha hidden ko hata kar visible kar diya */
}
.section-title {
  background: #f0f4f8; padding: 8px 12px; display: flex; align-items: center; gap: 6px;
  border-bottom: 2px solid var(--theme-color, #0088cc);
  /* Kyunki humne form-section se overflow hidden hataya, isliye title ke corners round karne honge */
  border-top-left-radius: 8px; 
  border-top-right-radius: 8px; 
}
        .section-title i { font-size: 13px; color: var(--theme-color, #0088cc); }
        .section-title span { font-size: 12px; font-weight: 600; color: #2c3e50; flex: 1; }

        .section-content { padding: 12px; background: white; }
        .form-row { display: grid; gap: 10px; margin-bottom: 10px; }
        .form-row.search-date-row { grid-template-columns: 2fr 1fr; }
        .form-row.two-col { grid-template-columns: 1fr 1fr; }
        .form-row.three-col { grid-template-columns: repeat(3, 1fr); }
        .form-group { display: flex; flex-direction: column; }
        .form-group label { font-size: 11px; font-weight: 600; color: #2c3e50; margin-bottom: 4px; display: flex; align-items: center; gap: 4px; }
        .form-group label i { color: var(--theme-color, #0088cc); font-size: 10px;}
        .required { color: #e74c3c; }

        .form-control-custom { width: 100%; padding: 6px 10px; border: 1.5px solid #cbd5e0; border-radius: 6px; font-size: 11px; transition: all 0.2s; outline: none; }
        .form-control-custom:focus { border-color: var(--theme-color, #0088cc); box-shadow: 0 0 0 2px rgba(0, 0, 0, 0.05); }
        .form-control-custom:disabled { background: #f3f4f6; opacity: 0.6; cursor: not-allowed; }
        .custom-textarea { resize: vertical; min-height: 30px; }

        .form-actions { display: flex; justify-content: flex-end; gap: 8px; margin-top: 12px; padding-top: 12px; border-top: 1px solid #e0e6ed; }
        .btn-primary, .btn-secondary { padding: 6px 16px; border-radius: 6px; font-size: 11px; font-weight: 600; border: none; cursor: pointer; transition: all 0.2s ease; display: flex; align-items: center; gap: 5px; }
        .btn-primary { background: var(--theme-color, #0088cc); color: white; }
        .btn-primary:hover:not(:disabled) { opacity: 0.9; transform: translateY(-1px); }
        .btn-secondary { background: #e0e6ed; color: #2c3e50; }
        .btn-secondary:hover:not(:disabled) { background: #cbd5e0; transform: translateY(-1px); }

        @media (max-width: 992px) {
          .form-row.two-col, .form-row.three-col, .form-row.search-date-row { grid-template-columns: 1fr; }
        }
      `}</style>
    </div>
  );
};

export default IpdAdvanceForm;